import React from 'react';
import { Wallet, Building, Users, Briefcase } from 'lucide-react';

const IndexTypes = () => {
  const indexTypes = [
    {
      icon: Wallet,
      title: 'Crypto Index',
      description: 'Diversified exposure to top cryptocurrencies including Bitcoin, Ethereum, and emerging altcoins.',
      assets: ['BTC', 'ETH', 'SOL', 'BNB', 'ADA'],
      riskLevel: 'High',
      riskColor: 'text-red-400',
      expectedReturn: '15-40%',
      gradient: 'from-orange-500/20 to-yellow-500/20'
    },
    {
      icon: Building,
      title: 'Forex Major Index',
      description: 'Track major currency pairs with stable, liquid forex markets for consistent returns.',
      assets: ['EUR/USD', 'GBP/USD', 'USD/JPY', 'USD/CHF'],
      riskLevel: 'Low',
      riskColor: 'text-emerald-400',
      expectedReturn: '5-12%',
      gradient: 'from-emerald-500/20 to-cyan-500/20'
    },
    {
      icon: Briefcase,
      title: 'CFD Commodities Index',
      description: 'Exposure to commodity CFDs including precious metals, oil, and agricultural products.',
      assets: ['Gold', 'Silver', 'Oil', 'Natural Gas'],
      riskLevel: 'Medium',
      riskColor: 'text-yellow-400',
      expectedReturn: '8-20%',
      gradient: 'from-yellow-500/20 to-orange-500/20'
    },
    {
      icon: Users,
      title: 'Balanced Multi-Asset',
      description: 'A blend of crypto, forex, and CFDs for optimal risk-adjusted returns across markets.',
      assets: ['Mixed Portfolio'],
      riskLevel: 'Medium',
      riskColor: 'text-yellow-400',
      expectedReturn: '10-25%',
      gradient: 'from-blue-500/20 to-purple-500/20'
    }
  ];

  return (
    <section className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
            <span className="text-cyan-400 text-sm font-medium">Index Categories</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Choose Your Investment Style
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Select from our range of index funds tailored to different risk appetites and investment goals.
          </p>
        </div>

        {/* Index Types Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {indexTypes.map((type, index) => {
            const IconComponent = type.icon;
            return (
              <div
                key={index}
                className="group relative bg-slate-800/30 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-500 overflow-hidden"
              >
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${type.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                
                <div className="relative z-10">
                  {/* Header */}
                  <div className="flex items-start mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <IconComponent className="w-7 h-7 text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white">{type.title}</h3>
                        <p className={`text-sm ${type.riskColor}`}>Risk: {type.riskLevel}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <p className="text-gray-400 mb-6 leading-relaxed">{type.description}</p>
                  
                  {/* Assets */}
                  <div className="flex flex-wrap gap-2">
                    {type.assets.map((asset, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-slate-700/50 rounded-full text-sm text-gray-300"
                      >
                        {asset}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default IndexTypes;
